import React, { useState } from 'react';
import { StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';

const TourReviews = ({ reviews, rating, tourId }) => {
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewFormData, setReviewFormData] = useState({
    rating: 5,
    comment: '',
  });
  const [formErrors, setFormErrors] = useState({});
  
  const queryClient = useQueryClient();
  
  // Submit review mutation
  const submitReviewMutation = useMutation(
    async (reviewData) => {
      // Get the token from localStorage
      const token = localStorage.getItem('token');
      
      // Check if token exists
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // Prepare the API URL
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      
      // Log the request for debugging
      console.log('Submitting review:', {
        tourId,
        reviewData,
        endpoint: `${apiUrl}/api/v1/tours/${tourId}/reviews`
      });
      
      // Make the API request
      const response = await axios.post(`${apiUrl}/api/v1/tours/${tourId}/reviews`, reviewData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      return response.data;
    },
    {
      onSuccess: (data) => {
        console.log('Review submitted successfully:', data);
        // Invalidate and refetch the tour data to show the new review
        queryClient.invalidateQueries(['tour', tourId.toString()]);
        setShowReviewForm(false);
        setReviewFormData({ rating: 5, comment: '' });
        alert('Thank you for your review!');
      },
      onError: (error) => {
        console.error('Review submission error:', error);
        
        // Handle specific error cases
        if (error.message === 'Authentication required') {
          alert('You need to be logged in to submit a review. Redirecting to login page...');
          localStorage.setItem('redirectAfterLogin', window.location.pathname);
          window.location.href = '/login';
          return;
        }
        
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          if (error.response.status === 422) {
            if (error.response.data && error.response.data.message) {
              alert(error.response.data.message);
            } else if (error.response.data && error.response.data.errors) {
              // Format validation errors
              const errorMessages = Object.values(error.response.data.errors)
                .flat()
                .join('\n');
              alert(`Validation errors:\n${errorMessages}`);
            } else {
              alert('You have already reviewed this tour or your review data is invalid.');
            }
          } else if (error.response.status === 401) {
            alert('Your session has expired. Please log in again.');
            localStorage.removeItem('token');
            window.location.href = '/login';
          } else {
            alert(`Error: ${error.response.data.message || 'An error occurred while submitting your review.'}`);
          }
        } else if (error.request) {
          // The request was made but no response was received
          alert('No response from server. Please check your internet connection and try again.');
        } else {
          // Something happened in setting up the request that triggered an Error
          alert('An error occurred while submitting your review. Please try again later.');
        }
      }
    }
  );
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setReviewFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleRatingChange = (newRating) => {
    setReviewFormData(prev => ({
      ...prev,
      rating: newRating
    }));
  };
  
  const validateForm = () => {
    const errors = {};
    
    if (reviewFormData.comment.trim().length < 10) {
      errors.comment = 'Review must be at least 10 characters long';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmitReview = (e) => {
    e.preventDefault();
    
    // Validate the form
    if (!validateForm()) {
      console.log('Form validation failed', formErrors);
      return;
    }
    
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      // Store the current location to redirect back after login
      localStorage.setItem('redirectAfterLogin', window.location.pathname);
      // Show an alert to the user
      alert('You need to be logged in to submit a review. Redirecting to login page...');
      // Redirect to login page
      window.location.href = '/login';
      return;
    }
    
    // Check if review is already being submitted
    if (submitReviewMutation.isLoading) {
      console.log('Review submission already in progress');
      return;
    }
    
    // Prepare the review data
    const reviewData = {
      ...reviewFormData,
      // Ensure rating is a number
      rating: parseInt(reviewFormData.rating, 10),
      // Trim the comment to remove extra whitespace
      comment: reviewFormData.comment.trim()
    };
    
    console.log('Submitting review data:', reviewData);
    
    // User is logged in, submit the review
    submitReviewMutation.mutate(reviewData);
  };
  
  // Generate star rating display
  const renderStarRating = (rating, interactive = false) => {
    const stars = [];
    
    for (let i = 1; i <= 5; i++) {
      if (interactive) {
        stars.push(
          <button
            key={i}
            type="button"
            onClick={() => handleRatingChange(i)}
            className="focus:outline-none"
          >
            {i <= reviewFormData.rating ? (
              <StarIconSolid className="h-6 w-6 text-yellow-500" />
            ) : (
              <StarIcon className="h-6 w-6 text-yellow-500" />
            )}
          </button>
        );
      } else {
        if (i <= rating) {
          stars.push(<StarIconSolid key={i} className="h-5 w-5 text-yellow-500" />);
        } else {
          stars.push(<StarIcon key={i} className="h-5 w-5 text-yellow-500" />);
        }
      }
    }
    
    return stars;
  };
  
  // Format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold">Tour Reviews</h3>
        <button
          onClick={() => setShowReviewForm(!showReviewForm)}
          className="btn-primary text-sm"
        >
          {showReviewForm ? 'Cancel' : 'Write a Review'}
        </button>
      </div>
      
      {/* Review summary */}
      <div className="bg-gray-50 rounded-lg p-6 mb-6">
        <div className="text-center md:text-left mb-4 md:mb-0">
          <div className="text-4xl font-bold text-gray-800">{rating.toFixed(1)}</div>
          <div className="flex justify-center md:justify-start mt-2">
            {renderStarRating(Math.round(rating))}
          </div>
          <div className="text-gray-500 mt-1">Based on {reviews.length} reviews</div>
        </div>
        
        {/* Rating distribution - this would be calculated from actual data in a real app */}
        <div className="w-full md:w-1/2 max-w-md">
          {[5, 4, 3, 2, 1].map((star) => {
            // Calculate percentage for each star rating
            const count = reviews.filter(review => Math.round(review.rating) === star).length;
            const percentage = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
            
            return (
              <div key={star} className="flex items-center mb-2">
                <div className="text-sm text-gray-600 w-8">{star}</div>
                <StarIconSolid className="h-4 w-4 text-yellow-500 mr-2" />
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-yellow-500 h-2.5 rounded-full"
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
                <div className="text-sm text-gray-600 ml-2 w-8">{count}</div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Review form */}
      {showReviewForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h4 className="text-lg font-semibold mb-4">Write Your Review</h4>
          <form onSubmit={handleSubmitReview}>
            <div className="mb-4">
              <label className="block text-gray-700 mb-2">Your Rating</label>
              <div className="flex">
                {renderStarRating(reviewFormData.rating, true)}
              </div>
            </div>
            <div className="mb-4">
              <label htmlFor="comment" className="block text-gray-700 mb-2">
                Your Review
              </label>
              <textarea
                id="comment"
                name="comment"
                rows="4"
                value={reviewFormData.comment}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border ${
                  formErrors.comment ? 'border-red-500' : 'border-gray-300'
                } rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                placeholder="Share your experience with this tour..."
              ></textarea>
              {formErrors.comment && (
                <p className="text-red-500 text-sm mt-1">{formErrors.comment}</p>
              )}
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                className="btn-primary"
                disabled={submitReviewMutation.isLoading}
              >
                {submitReviewMutation.isLoading ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Reviews list */}
      <div className="space-y-6">
        {reviews.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No reviews yet. Be the first to review this tour!</p>
          </div>
        ) : (
          reviews.map((review) => (
            <div key={review.id} className="border-b border-gray-200 pb-6">
              <div className="flex items-start">
                <img
                  src={review.user?.profile_photo || 'https://via.placeholder.com/150'}
                  alt={review.user?.name || 'Anonymous User'}
                  className="w-12 h-12 rounded-full mr-4 object-cover"
                />
                <div className="flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
                    <h5 className="font-semibold text-gray-800">{review.user?.name || 'Anonymous User'}</h5>
                    <span className="text-sm text-gray-500">{formatDate(review.created_at || review.date)}</span>
                  </div>
                  <div className="flex mb-3">
                    {renderStarRating(review.rating)}
                  </div>
                  <p className="text-gray-600">{review.comment}</p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TourReviews;
